import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronDown, ChevronRight, Check, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...classes: Array<string | false | null | undefined>) => twMerge(clsx(classes));

export function PageHeader({ title, actions, subtitle }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold wrap-anywhere">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'brand' | 'warn' | 'danger' | 'info' }) {
  const tones = {
    neutral: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
    brand: 'bg-brand-50 text-brand-700 dark:bg-brand-700/30 dark:text-brand-100',
    warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  } as const;
  return <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>{children}</span>;
}

/** Modal rendered in a portal (MRLCO standard: CRUD happens in modals). */
export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={title} className={cn('card flex max-h-[90vh] w-full flex-col', wide ? 'max-w-4xl' : 'max-w-lg')}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={onClose} aria-label={t('app.close')}>
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-3 dark:border-neutral-800">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Field({ label, children, hint, error, required }: { label: string; children: ReactNode; hint?: string; error?: string; required?: boolean }) {
  return (
    <label className="block">
      <span className="label">
        {label}
        {required && <span className="ms-1 text-red-500">*</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-neutral-500">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

/**
 * Card that opens a record (the card grids): the whole surface is the target, with a pointer, a brand border and a
 * lift on hover, Enter when focused, and a "View details" cue closing the footer. Links and buttons placed inside
 * must stop propagation so they keep their own action.
 */
export function ClickableCard({ onOpen, label, children, footer }: { onOpen: () => void; label: string; children: ReactNode; footer?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={label}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(); }}
      className="card group flex cursor-pointer flex-col p-4 transition hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-md focus-visible:outline-2 focus-visible:outline-brand-500 dark:hover:border-brand-500"
    >
      {children}
      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-3 text-xs text-neutral-500">
        <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">{footer}</span>
        <span className="ms-auto inline-flex shrink-0 items-center gap-0.5 font-medium text-brand-700 group-hover:underline dark:text-brand-100">{t('app.viewDetails')}<ChevronRight size={14} className="rtl:rotate-180" /></span>
      </div>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-neutral-200 dark:bg-neutral-800', className)} />;
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="card p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="mb-3 h-6 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="card p-10 text-center text-sm text-neutral-500 dark:text-neutral-400">{children}</div>;
}

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

/** Searchable select (MRLCO standard: never a native <select>). Portal + fixed positioning so it never clips inside modals. */
export function SearchableSelect({ value, onChange, options, placeholder, multiple, disabled }: { value: string | string[]; onChange: (v: string | string[]) => void; options: SelectOption[]; placeholder?: string; multiple?: boolean; disabled?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const anchor = useRef<HTMLButtonElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const isRtl = document.documentElement.dir === 'rtl';

  useEffect(() => {
    if (!open) return;
    const update = () => setRect(anchor.current?.getBoundingClientRect() ?? null);
    update();
    const close = (e: MouseEvent) => {
      if (anchor.current?.contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest('[data-select-menu]')) return;
      setOpen(false);
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('mousedown', close);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('mousedown', close);
    };
  }, [open]);

  const filtered = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()) || o.value.toLowerCase().includes(q.toLowerCase()));
  const toggle = (v: string) => {
    if (multiple) {
      onChange(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]);
    } else {
      onChange(v);
      setOpen(false);
    }
  };
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  return (
    <>
      <button type="button" ref={anchor} disabled={disabled} onClick={() => setOpen((o) => !o)} className={cn('input flex items-center justify-between text-start', disabled && 'opacity-60')}>
        <span className={cn('truncate', selected.length === 0 && 'text-neutral-400')}>{selected.length ? selected.map(labelFor).join(', ') : (placeholder ?? '—')}</span>
        <ChevronDown size={16} className="shrink-0 text-neutral-400" />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            data-select-menu
            className="card fixed z-[60] max-h-72 overflow-hidden"
            style={{ top: rect.bottom + 4, width: rect.width, ...(isRtl ? { right: window.innerWidth - rect.right } : { left: rect.left }) }}
          >
            <div className="flex items-center gap-2 border-b border-neutral-200 px-2 py-1.5 dark:border-neutral-800">
              <Search size={14} className="text-neutral-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('app.search')} className="w-full bg-transparent text-sm outline-none" />
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 && <li className="px-3 py-2 text-sm text-neutral-500">{t('app.noResults')}</li>}
              {filtered.map((o) => (
                <li key={o.value}>
                  <button type="button" onClick={() => toggle(o.value)} className={cn('flex w-full items-center justify-between px-3 py-1.5 text-start text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800', selected.includes(o.value) && 'font-medium text-brand-700 dark:text-brand-100')}>
                    <span>
                      {o.label}
                      {o.description && <span className="ms-2 text-xs text-neutral-500">{o.description}</span>}
                    </span>
                    {selected.includes(o.value) && <Check size={14} />}
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}

export function ConfirmDialog({ open, title, body, onConfirm, onCancel, danger }: { open: boolean; title: string; body?: string; onConfirm: () => void; onCancel: () => void; danger?: boolean }) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {t('app.cancel')}
          </button>
          <button type="button" className={danger ? 'btn-danger' : 'btn-primary'} onClick={onConfirm}>
            {t('app.confirm')}
          </button>
        </>
      }
    >
      {body && <p className="text-sm">{body}</p>}
    </Modal>
  );
}
